import { Children, cloneElement, isValidElement, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

type ListCardProps = {
  children: ReactNode;
  className?: string;
};

export const ListCard = ({ children, className }: ListCardProps) => {
  const items = Children.toArray(children);
  return (
    <div className={cn("overflow-hidden rounded-lg border border-neutral-200 bg-white", className)}>
      {items.map((child, index) =>
        isValidElement<{ first?: boolean }>(child) && child.type === ListRow
          ? cloneElement(child, { first: index === 0 })
          : child,
      )}
    </div>
  );
};

type ListRowProps = {
  title: string;
  subtitle?: string;
  detail?: string;
  status?: ReactNode;
  icon?: LucideIcon;
  to?: string;
  onClick?: () => void;
  destructive?: boolean;
  testID?: string;
  first?: boolean;
};

export const ListRow = ({ title, subtitle, detail, status, icon: Icon, to, onClick, destructive, testID, first = false }: ListRowProps) => {
  const inner = (
    <>
      {Icon ? (
        <Icon className={cn("h-5 w-5 shrink-0", destructive ? "text-error" : "text-neutral-700")} strokeWidth={2} />
      ) : null}
      <div className="min-w-0 flex-1">
        <p className={cn("text-body font-semibold", destructive ? "text-error" : "text-neutral-900")}>{title}</p>
        {subtitle ? <p className="text-body-sm text-neutral-500">{subtitle}</p> : null}
        {detail ? <p className="mt-1 text-body-sm text-neutral-500">{detail}</p> : null}
      </div>
      {status}
      {to || onClick ? <ChevronRight className="h-5 w-5 text-neutral-400" strokeWidth={2} /> : null}
    </>
  );

  const className = cn(
    "flex min-h-16 items-center gap-3 px-4 py-3",
    !first && "border-t border-neutral-100",
  );

  if (to) {
    return (
      <Link to={to} className={className}>
        {inner}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button type="button" onClick={onClick} data-testid={testID} className={cn(className, "w-full text-left")}>
        {inner}
      </button>
    );
  }

  return <div className={className}>{inner}</div>;
};
